import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoCpfOuCnpj,
  CampoDeSelecao,
  Cartao,
  EstadoVazio,
  Modal,
  Tabela,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { mascararCpfOuCnpj } from '../../servicos/documento';
import { listarDocumentos, type DocumentoDaEmpresa } from '../../servicos/empresas';
import { baixarArquivo, requisicaoAutenticada } from '../../servicos/http';
import type { DadosBancarios } from '../../servicos/escrita';
import { useSalvarDadosBancarios } from '../../hooks/useEscrita';
import { useIdentidade } from '../../hooks/useSessao';
import { CadastroDaEmpresa, DesativacaoDaEmpresa } from '../CadastroDaEmpresa';
import { DriveDaEmpresa } from '../DriveDaEmpresa';
import { ErroDaApi } from '../ErroDaApi';

/**
 * A aba Cadastro de uma empresa: quem ela é, onde fica, para onde se paga, o
 * que ela anexou e onde estão os arquivos dela no Drive.
 *
 * ## Uma aba, várias seções — para o cliente e para a empresa
 *
 * Esta é a mesma aba do perfil do cliente e da tela de empresa (fornecedor,
 * assessor, qualquer cadastro). Antes, a tela de empresa tinha Documentos,
 * Dados bancários e Drive como abas próprias e o perfil do cliente tinha as
 * duas primeiras empilhadas em versões diferentes (documentos só de leitura,
 * sem baixar). Duas implementações de cada coisa é a receita para uma ficar
 * para trás — e foi o que aconteceu com o download.
 *
 * ## Quem pode o quê
 *
 * Como antes: a operadora (admin) envia, exclui e vincula Drive; o usuário da
 * empresa vê e baixa documentos e não vê o Drive. É o mesmo recorte que as
 * duas telas antigas tinham — a de empresa (só admin) com tudo, o perfil do
 * cliente só leitura. O controle de verdade é da API; aqui só não se oferece o
 * botão que daria 403.
 *
 * ## A ordem
 *
 * Identificação e endereço primeiro, depois banco, documentos e Drive; o cartão
 * de desativar fica **por último**, fora do formulário: é a única ação que não
 * se desfaz clicando "salvar" de novo, e no meio da página ficaria a um clique
 * de quem só rolou para ver o CEP.
 *
 * ## O que arrumei em relação ao Deca 1
 *
 * Lá o endereço aparecia como um bloco de texto com `null` no meio da linha
 * ("Rua R SETE DE SETEMBRO, 178 - null"). Campo vazio vira travessão aqui — um
 * `null` na tela é um defeito visível que o operador aprende a ignorar, e
 * depois ele deixa de notar quando o dado realmente falta.
 */
export function AbaCadastro(props: {
  readonly businessId: string;
  /** Para onde ir depois de desativar — ver `CadastroDaEmpresa`. */
  readonly rotaAposDesativar?: string;
  /**
   * Quem olha, quando não é a operação:
   *
   * - `gestao` — o assessor do cliente: edita identificação, endereço, dados
   *   fiscais e bancários, e baixa os documentos. Desativar a empresa e mexer
   *   no Drive continuam sendo da operação;
   * - `leitura` — o fornecedor do cliente: só os dados da empresa. Conta
   *   bancária e documentos são do cliente e da operação.
   *
   * Ausente = a operação, com tudo.
   */
  readonly modo?: 'gestao' | 'leitura';
}): JSX.Element {
  const gerencia = useGerenciaEmpresa();
  const modo = props.modo ?? 'operacao';

  return (
    <div className="flex flex-col gap-6">
      {/*
        Identificação, endereço e dados fiscais são **editáveis** aqui.
        Eram só leitura, e o cliente que mudava de endereço só mudava por SQL —
        o mesmo formulário da tela de empresas, porque é o mesmo cadastro.
      */}
      <CadastroDaEmpresa
        businessId={props.businessId}
        ocultarDesativacao
        somenteLeitura={modo === 'leitura'}
      />

      {modo !== 'leitura' && (
        <>
          <DadosBancariosDaEmpresa businessId={props.businessId} />
          <DocumentosDaEmpresa businessId={props.businessId} podeGerenciar={gerencia} />
        </>
      )}
      {gerencia && <DriveDaEmpresa businessId={props.businessId} />}

      {modo === 'operacao' && (
        <DesativacaoDaEmpresa
          businessId={props.businessId}
          {...(props.rotaAposDesativar === undefined
            ? {}
            : { rotaAposDesativar: props.rotaAposDesativar })}
        />
      )}
    </div>
  );
}

/**
 * Quem gerencia o cadastro na tela: a operadora. O gestor da empresa também
 * passaria em `exigirGerenciar` no servidor, mas a tela do cliente sempre foi
 * só leitura para ele — abrir upload e Drive para o cliente é decisão de
 * produto, não desta reorganização.
 */
function useGerenciaEmpresa(): boolean {
  const identidade = useIdentidade();
  return identidade?.isAdmin === true;
}

/* ─── Dados bancários ─────────────────────────────────────────────────────── */

function DadosBancariosDaEmpresa(props: { readonly businessId: string }): JSX.Element {
  const consulta = useQuery({
    queryKey: ['dados-bancarios', props.businessId],
    queryFn: () =>
      requisicaoAutenticada<DadosBancarios | null>(`/business/${props.businessId}/dados-bancarios`),
  });
  const salvar = useSalvarDadosBancarios(props.businessId);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<DadosBancarios | null>(null);

  const atual = form ?? consulta.data ?? {};
  const vazio =
    consulta.data === null ||
    (consulta.data !== undefined && Object.values(consulta.data).every((v) => !v));

  const campo = (chave: keyof DadosBancarios, rotulo: string, ajuda?: string): JSX.Element => (
    <Campo
      rotulo={rotulo}
      {...(ajuda === undefined ? {} : { ajuda })}
      value={atual[chave] ?? ''}
      onChange={(ev) => setForm({ ...atual, [chave]: ev.target.value })}
    />
  );

  return (
    <Cartao
      como="section"
      titulo="Dados bancários"
      acao={
        editando ? undefined : (
          <Botao variante="secundario" tamanho="pequeno" onClick={() => setEditando(true)}>
            {vazio ? 'Adicionar dados' : 'Editar'}
          </Botao>
        )
      }
    >
      {editando ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            salvar.mutate(atual, { onSuccess: () => setEditando(false) });
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {campo('banco', 'Banco')}
            {campo('agencia', 'Agência')}
            {campo('conta', 'Conta')}
            {campo('pix', 'Chave PIX')}
            <CampoCpfOuCnpj
              rotulo="CPF/CNPJ do titular"
              value={atual.taxpayerId ?? ''}
              onChange={(valor) => setForm({ ...atual, taxpayerId: valor })}
            />
          </div>

          {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}

          <div className="flex justify-end gap-3">
            <Botao
              variante="secundario"
              onClick={() => {
                setForm(null);
                setEditando(false);
              }}
            >
              Cancelar
            </Botao>
            <Botao type="submit" carregando={salvar.isPending}>
              Salvar
            </Botao>
          </div>
        </form>
      ) : vazio ? (
        <p className="text-sm text-conteudo-sutil">Não há dados bancários cadastrados.</p>
      ) : (
        <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <Dado rotulo="Banco" valor={consulta.data?.banco ?? null} />
          <Dado rotulo="Agência" valor={consulta.data?.agencia ?? null} fiscal />
          <Dado rotulo="Conta" valor={consulta.data?.conta ?? null} fiscal />
          <Dado rotulo="Chave PIX" valor={consulta.data?.pix ?? null} />
          <Dado
            rotulo="CPF/CNPJ do titular"
            valor={consulta.data?.taxpayerId ? mascararCpfOuCnpj(consulta.data.taxpayerId) : null}
            fiscal
          />
        </dl>
      )}
    </Cartao>
  );
}

/* ─── Documentos ──────────────────────────────────────────────────────────── */

/**
 * Documentos cadastrais da empresa.
 *
 * ## Um documento por tipo
 *
 * A chave é (empresa, tipo): enviar de novo o mesmo tipo substitui. É o desenho
 * do banco (`@@unique([businessId, type])`) e faz sentido — "contrato social" é
 * um documento, não uma pilha. A tela avisa antes de substituir.
 *
 * ## O nome do arquivo não aparece cru
 *
 * A coluna "Arquivo" imprimia o `fileLocation` — `s3://bucket/uuid-contrato.pdf`
 * na cara do operador, sem jeito de abrir. O download é uma rota própria, e o
 * que se mostra é o tipo traduzido.
 */
export function DocumentosDaEmpresa(props: {
  readonly businessId: string;
  readonly podeGerenciar: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const [enviando, setEnviando] = useState(false);

  const consulta = useQuery({
    queryKey: ['documentos', props.businessId],
    queryFn: () => listarDocumentos(props.businessId),
  });

  const excluir = useMutation({
    mutationFn: (documentoId: string) =>
      requisicaoAutenticada<void>(`/business/documentos/${documentoId}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documentos', props.businessId] });
    },
  });

  const colunas: readonly ColunaTabela<DocumentoDaEmpresa>[] = [
    {
      chave: 'type',
      titulo: 'Tipo',
      larguraMinima: '16rem',
      celula: (d) => (
        <span className="text-sm text-conteudo-primario">{humanizarTipo(d.type)}</span>
      ),
    },
    {
      chave: 'tamanho',
      titulo: 'Tamanho',
      numerica: true,
      larguraMinima: '8rem',
      celula: (d) => (
        <span className="valor-fiscal text-sm text-conteudo-secundario">
          {formatarTamanho(d.fileSizeBytes)}
        </span>
      ),
    },
    {
      chave: 'observation',
      titulo: 'Observação',
      celula: (d) => d.observation ?? <span className="text-conteudo-sutil">—</span>,
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '12rem',
      celula: (d) => (
        <span className="flex flex-wrap justify-end gap-2">
          <Botao
            variante="secundario"
            tamanho="pequeno"
            onClick={() => {
              // O nome real vem do `Content-Disposition`; este é só o fallback.
              void baixarArquivo(
                `/business/documentos/${d.id}/download`,
                d.type.includes('.') ? d.type : `${d.type.toLowerCase()}.pdf`,
              );
            }}
          >
            Baixar
          </Botao>
          {props.podeGerenciar && (
            <Botao
              variante="sutil"
              tamanho="pequeno"
              onClick={async () => {
                const ok = await confirmar({
                  titulo: `Excluir "${humanizarTipo(d.type)}"?`,
                  descricao:
                    'O vínculo sai do cadastro da empresa; o arquivo em si continua no armazenamento.',
                  rotuloConfirmar: 'Excluir',
                  tom: 'perigo',
                });
                if (ok) excluir.mutate(d.id);
              }}
            >
              Excluir
            </Botao>
          )}
        </span>
      ),
    },
  ];

  const documentos = consulta.data ?? [];

  return (
    <Cartao
      como="section"
      titulo="Documentos"
      subtitulo="Contrato social, cartão CNPJ, procuração. Um documento por tipo — enviar o mesmo tipo de novo substitui o anterior."
      acao={
        props.podeGerenciar ? (
          <Botao tamanho="pequeno" onClick={() => setEnviando(true)}>
            Enviar documento
          </Botao>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}

        {!consulta.isLoading && documentos.length === 0 ? (
          <EstadoVazio
            titulo="Nenhum documento"
            descricao="Não há documentos cadastrais anexados a esta empresa."
            {...(props.podeGerenciar
              ? { acao: <Botao onClick={() => setEnviando(true)}>Enviar documento</Botao> }
              : {})}
          />
        ) : (
          <Tabela
            legenda="Documentos cadastrais"
            colunas={colunas}
            dados={documentos}
            chaveDaLinha={(d) => d.id}
            chaveDeCabecalhoDaLinha="type"
            carregando={consulta.isLoading}
          />
        )}

        {props.podeGerenciar && (
          <ModalDeDocumento
            businessId={props.businessId}
            aberto={enviando}
            tiposExistentes={documentos.map((d) => d.type)}
            aoFechar={() => setEnviando(false)}
          />
        )}
      </div>
    </Cartao>
  );
}

const TIPOS_DE_DOCUMENTO = [
  { valor: 'CONTRATO_SOCIAL', rotulo: 'Contrato social' },
  { valor: 'CARTAO_CNPJ', rotulo: 'Cartão CNPJ' },
  { valor: 'PROCURACAO', rotulo: 'Procuração' },
  { valor: 'DOCUMENTO_REPRESENTANTE', rotulo: 'Documento do representante' },
  { valor: 'CERTIDAO_NEGATIVA', rotulo: 'Certidão negativa' },
  { valor: 'OUTROS', rotulo: 'Outro documento' },
] as const;

function ModalDeDocumento(props: {
  readonly businessId: string;
  readonly aberto: boolean;
  readonly tiposExistentes: readonly string[];
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState<string>(TIPOS_DE_DOCUMENTO[0].valor);
  const [observacao, setObservacao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);

  const enviar = useMutation({
    mutationFn: async () => {
      if (arquivo === null) throw new Error('Escolha um arquivo.');
      const conteudoBase64 = await lerComoBase64(arquivo);

      return requisicaoAutenticada<unknown>(`/business/${props.businessId}/documentos`, {
        metodo: 'POST',
        corpo: {
          type: tipo,
          nome: arquivo.name,
          conteudoBase64,
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
          ...(observacao.trim() === '' ? {} : { observation: observacao.trim() }),
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documentos', props.businessId] });
      fechar();
    },
  });

  const fechar = (): void => {
    enviar.reset();
    setArquivo(null);
    setObservacao('');
    props.aoFechar();
  };

  const substitui = props.tiposExistentes.includes(tipo);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Enviar documento"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => enviar.mutate()}
            disabled={arquivo === null}
            carregando={enviar.isPending}
          >
            {substitui ? 'Substituir' : 'Enviar'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoDeSelecao
          rotulo="Tipo de documento"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          opcoes={TIPOS_DE_DOCUMENTO.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))}
        />

        {substitui && (
          <Aviso tom="atencao" titulo="Já existe um documento deste tipo">
            O cadastro guarda um por tipo. Enviar substitui o atual.
          </Aviso>
        )}

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Arquivo</span>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
          />
        </label>

        <Campo
          rotulo="Observação"
          ajuda="Opcional — data de emissão, validade, o que ajudar quem for conferir."
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
        />

        {enviar.isError && <ErroDaApi titulo="Não foi possível enviar" erro={enviar.error} />}
      </div>
    </Modal>
  );
}

/**
 * O `type` guarda duas coisas nos dados reais.
 *
 * Os 346 documentos migrados têm o **nome do arquivo** ali
 * (`2ª Alteração Contratual Consolidada.pdf`), não um código — o legado
 * gravava assim. Os novos usam código (`CONTRATO_SOCIAL`).
 *
 * Só o código é traduzido. Passar o nome de arquivo pelo mesmo tratamento o
 * transformaria em `2ª alteração contratual consolidada.pdf`, destruindo a
 * capitalização de um nome que alguém escreveu.
 */
function humanizarTipo(tipo: string): string {
  const conhecido = TIPOS_DE_DOCUMENTO.find((t) => t.valor === tipo);
  if (conhecido !== undefined) return conhecido.rotulo;
  if (!/^[A-Z0-9_]+$/.test(tipo)) return tipo;

  const texto = tipo.replace(/_/g, ' ').toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function formatarTamanho(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** `FileReader` devolve `data:...;base64,XXX` — só o depois da vírgula interessa. */
async function lerComoBase64(arquivo: File): Promise<string> {
  const leitor = new FileReader();
  return new Promise((resolver, rejeitar) => {
    leitor.onload = () => {
      const resultado = String(leitor.result);
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/* ─── Peças ───────────────────────────────────────────────────────────────── */

/**
 * Um par rótulo/valor de leitura. Campo vazio vira travessão — ver a nota da
 * aba sobre o `null` na tela do Deca 1.
 */
function Dado(props: {
  readonly rotulo: string;
  readonly valor: string | null;
  readonly fiscal?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-conteudo-secundario">
        {props.rotulo}
      </dt>
      <dd
        className={`mt-1 text-sm ${
          props.valor === null || props.valor === ''
            ? 'text-conteudo-sutil'
            : `text-conteudo-primario ${props.fiscal === true ? 'valor-fiscal' : ''}`
        }`}
      >
        {props.valor === null || props.valor === '' ? '—' : props.valor}
      </dd>
    </div>
  );
}
